import { useEffect, useRef, useState } from 'react';
import type { CSSProperties, PointerEvent as ReactPointerEvent } from 'react';
import { mono, sans } from '../../ui/atoms.js';
import type { RoundCopy } from '../../ui/roundText.js';

/**
 * The saboteur's game, third version (games/Hack.tsx): crack the lock. Three numbers are shown;
 * turn the safe's dial to each in turn and press SET, and the lock opens — that is the solve, with
 * no code after it. The dial turns with big − and + buttons (hold one to keep turning) or by
 * dragging the dial round, and it follows the finger.
 */

/** The numbers round the dial, 0 to 39. */
export const DIAL = 40;
const STEP_DEG = 360 / DIAL;
/** Each number is at least this far round from the one before (and the first from 0): always a real turn. */
export const MIN_TURN = 8;
/** Hold − or +: one step at once, then after a moment a steady run. */
const HOLD_WAIT_MS = 380;
const HOLD_EVERY_MS = 110;

export const wrapDial = (n: number) => ((n % DIAL) + DIAL) % DIAL;

/** How far apart two numbers are the short way round. */
export function dialGap(a: number, b: number): number {
  const d = Math.abs(wrapDial(a) - wrapDial(b));
  return Math.min(d, DIAL - d);
}

/** Three fresh numbers every game, each a turn from the last and none the same. */
export function dealDial(random: () => number = Math.random): number[] {
  const out: number[] = [];
  let from = 0;
  for (let guard = 0; out.length < 3 && guard < 500; guard++) {
    const n = Math.floor(random() * DIAL);
    if (dialGap(n, from) < MIN_TURN || out.includes(n)) continue;
    out.push(n);
    from = n;
  }
  // Hardly ever needed: a steady third of the way round each time.
  while (out.length < 3) {
    from = wrapDial(from + 13);
    out.push(from);
  }
  return out;
}

const two = (n: number) => String(n).padStart(2, '0');

export function DialBoard({
  r,
  onDone,
  onStep,
  onMistake,
}: {
  r: RoundCopy;
  onDone: () => void;
  /** Each number set: how many are set now. */
  onStep?: (set: number) => void;
  /** SET pressed off the number. */
  onMistake?: () => void;
}) {
  const [targets] = useState(() => dealDial());
  // Counted without wrapping, so the face never spins back the long way when 39 turns to 0.
  const [pos, setPos] = useState(0);
  const [set, setSet] = useState(0);
  const [note, setNote] = useState<string | null>(null);
  const hold = useRef<{ wait?: number; every?: number }>({});
  const drag = useRef<{ id: number; last: number; turned: number; from: number } | null>(null);
  const value = wrapDial(pos);

  const turn = (by: number) => {
    setPos((p) => p + by);
    setNote(null);
  };
  const stop = () => {
    window.clearTimeout(hold.current.wait);
    window.clearInterval(hold.current.every);
    hold.current = {};
  };
  useEffect(() => stop, []);

  /** A − or + button: a step on the press, then a steady run while it is held. */
  const holdToTurn = (by: number) => ({
    onPointerDown: (e: ReactPointerEvent<HTMLButtonElement>) => {
      e.preventDefault();
      stop();
      turn(by);
      hold.current.wait = window.setTimeout(() => {
        hold.current.every = window.setInterval(() => turn(by), HOLD_EVERY_MS);
      }, HOLD_WAIT_MS);
    },
    onPointerUp: stop,
    onPointerLeave: stop,
    onPointerCancel: stop,
    onContextMenu: (e: { preventDefault: () => void }) => e.preventDefault(),
    // A keyboard press has no pointer before it.
    onClick: (e: { detail: number }) => {
      if (e.detail === 0) turn(by);
    },
  });

  // Dragging the dial: the face turns with the finger, one number for every 9°.
  const angle = (e: ReactPointerEvent<HTMLDivElement>) => {
    const box = e.currentTarget.getBoundingClientRect();
    return (Math.atan2(e.clientY - (box.top + box.height / 2), e.clientX - (box.left + box.width / 2)) * 180) / Math.PI;
  };
  const down = (e: ReactPointerEvent<HTMLDivElement>) => {
    stop();
    drag.current = { id: e.pointerId, last: angle(e), turned: 0, from: pos };
    try {
      e.currentTarget.setPointerCapture(e.pointerId);
    } catch {
      /* the finger already left; nothing to follow */
    }
  };
  const move = (e: ReactPointerEvent<HTMLDivElement>) => {
    const d = drag.current;
    if (!d || d.id !== e.pointerId) return;
    const a = angle(e);
    // The short way from the last reading, so passing ±180° is no jump.
    d.turned += ((a - d.last + 540) % 360) - 180;
    d.last = a;
    // Clockwise brings the lower numbers up to the mark, as on a real dial.
    setPos(d.from - Math.round(d.turned / STEP_DEG));
    setNote(null);
  };
  const up = () => {
    drag.current = null;
  };

  const press = () => {
    stop();
    const target = targets[set]!;
    if (value !== target) {
      setNote(r.hackDialFirst(two(target)));
      onMistake?.();
      return;
    }
    navigator.vibrate?.(15);
    setNote(null);
    setSet(set + 1);
    onStep?.(set + 1);
    if (set + 1 === targets.length) onDone();
  };

  return (
    <>
      <div style={{ fontFamily: sans, fontSize: 20, fontWeight: 600, lineHeight: 1.3 }}>{r.hackDial}</div>
      <div style={{ marginTop: 14, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}>
        {targets.map((t, i) => (
          <span key={i} style={{ display: 'contents' }}>
            {i > 0 ? <span style={{ fontFamily: mono, fontSize: 22, color: '#5d6b78' }}>·</span> : null}
            <span style={chip(i < set, i === set)}>{two(t)}</span>
          </span>
        ))}
      </div>
      <div
        data-nodrag
        onPointerDown={down}
        onPointerMove={move}
        onPointerUp={up}
        onPointerCancel={up}
        style={{
          position: 'relative',
          width: 216,
          height: 216,
          margin: '18px auto 0',
          touchAction: 'none',
          userSelect: 'none',
          WebkitUserSelect: 'none',
          cursor: 'grab',
        }}
      >
        <svg
          viewBox="-100 -100 200 200"
          style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', transform: `rotate(${-pos * STEP_DEG}deg)`, transition: 'transform .1s ease-out' }}
        >
          <circle r={97} fill="#0b0f15" stroke="rgba(255,255,255,.3)" strokeWidth={2} />
          {Array.from({ length: DIAL }, (_, n) => {
            const big = n % 5 === 0;
            return (
              <line
                key={n}
                x1={0}
                y1={big ? -78 : -84}
                x2={0}
                y2={-92}
                stroke={big ? '#dfe9f2' : 'rgba(223,233,242,.45)'}
                strokeWidth={big ? 2.5 : 1.5}
                transform={`rotate(${n * STEP_DEG})`}
              />
            );
          })}
          {Array.from({ length: DIAL / 5 }, (_, i) => i * 5).map((n) => (
            <text
              key={n}
              x={0}
              y={-60}
              transform={`rotate(${n * STEP_DEG})`}
              textAnchor="middle"
              dominantBaseline="middle"
              fill="#dfe9f2"
              style={{ fontFamily: mono, fontSize: 15, fontWeight: 700 }}
            >
              {n}
            </text>
          ))}
          <circle r={42} fill="#121821" stroke="rgba(255,255,255,.12)" strokeWidth={2} />
        </svg>
        {/* The mark the number is read against, and the number itself: neither turns. */}
        <svg viewBox="0 0 20 14" style={{ position: 'absolute', left: '50%', top: -12, width: 20, height: 14, marginLeft: -10 }}>
          <path d="M0 0 H20 L10 14 Z" fill="#ff5f86" />
        </svg>
        <div
          style={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            pointerEvents: 'none',
            fontFamily: mono,
            fontSize: 36,
            fontWeight: 700,
            color: '#ffffff',
          }}
        >
          {two(value)}
        </div>
      </div>
      <div style={{ minHeight: 26, marginTop: 10, textAlign: 'center', fontFamily: sans, fontSize: 18, fontWeight: 600, color: '#ffd1da' }}>
        {note ?? ''}
      </div>
      <div style={{ marginTop: 6, display: 'grid', gridTemplateColumns: '76px 1fr 76px', gap: 10 }}>
        <button {...holdToTurn(-1)} style={turnButton}>
          −
        </button>
        <button onClick={press} style={setButton}>
          {r.hackDialSet}
        </button>
        <button {...holdToTurn(1)} style={turnButton}>
          +
        </button>
      </div>
    </>
  );
}

/** One of the three numbers: filled once set, outlined while it is the one to turn to. */
function chip(done: boolean, now: boolean): CSSProperties {
  return {
    minWidth: 62,
    padding: '6px 8px',
    boxSizing: 'border-box',
    borderRadius: 12,
    border: `2px solid ${now ? '#ffffff' : done ? '#ff5f86' : 'rgba(255,255,255,.2)'}`,
    background: done ? '#ff5f86' : 'transparent',
    color: done ? '#2a0610' : now ? '#ffffff' : '#9db3c2',
    fontFamily: mono,
    fontSize: 26,
    fontWeight: 700,
    textAlign: 'center',
  };
}

const turnButton: CSSProperties = {
  minHeight: 64,
  borderRadius: 14,
  border: '1px solid rgba(255,255,255,.2)',
  background: 'rgba(255,255,255,.06)',
  color: '#ffffff',
  fontFamily: mono,
  fontSize: 34,
  fontWeight: 700,
  touchAction: 'none',
  userSelect: 'none',
  WebkitUserSelect: 'none',
  WebkitTouchCallout: 'none',
};

const setButton: CSSProperties = {
  minHeight: 64,
  borderRadius: 14,
  border: '3px solid #ff5f86',
  background: 'rgba(255,95,134,.14)',
  color: '#ffffff',
  fontFamily: sans,
  fontSize: 24,
  fontWeight: 800,
  letterSpacing: 2,
};
